import { useTranslation } from 'react-i18next'

import { Alert, FormField, Select, type SelectOption } from '@percent/lemonade'

export type FundraiserVisibility = 'listed' | 'public' | 'unlisted'
export type OpportunityVisibility = 'listed' | 'unlisted'

type FundraiserPrivacySettingsProps = {
  entityType?: 'fundraiser'
  value: FundraiserVisibility
  onChange: (value: FundraiserVisibility) => void
  canMakePublic?: boolean
  showPublicPermissionAlert?: boolean
  partnerName: string
  description?: string
}

type OpportunityPrivacySettingsProps = {
  entityType: 'opportunity'
  value: OpportunityVisibility
  onChange: (value: OpportunityVisibility) => void
  canMakePublic?: never
  showPublicPermissionAlert?: never
  partnerName: string
  description?: string
}

export type PrivacySettingsProps = (FundraiserPrivacySettingsProps | OpportunityPrivacySettingsProps) & {
  touched?: boolean
  error?: string
  sectionTitleClassName?: string
}

export function PrivacySettings(props: Readonly<PrivacySettingsProps>) {
  const { touched, error, canMakePublic = true, value, onChange } = props
  const { t } = useTranslation()

  const { showPublicPermissionAlert = false, partnerName, entityType = 'fundraiser', description } = props

  const FUNDRAISER_OPTIONS: SelectOption[] = [
    {
      value: 'listed',
      label: t('workplace_giving.wizard.fundraiser.privacySettings.optionListed', { partnerName }) as string,
      description: t('workplace_giving.wizard.fundraiser.privacySettings.optionListedDescription') as string,
      boldLabel: true
    },
    {
      value: 'public',
      label: t('workplace_giving.wizard.fundraiser.privacySettings.optionPublic') as string,
      description: t('workplace_giving.wizard.fundraiser.privacySettings.optionPublicDescription-2') as string,
      boldLabel: true
    },
    {
      value: 'unlisted',
      label: t('workplace_giving.wizard.fundraiser.privacySettings.optionUnlisted') as string,
      description: t('workplace_giving.wizard.fundraiser.privacySettings.optionUnlistedDescription') as string,
      boldLabel: true
    }
  ]

  const OPPORTUNITY_OPTIONS: SelectOption[] = [
    {
      value: 'listed',
      label: t('workplace_giving.wizard.opportunity.privacySettings.optionListed', { partnerName }) as string,
      description: t('workplace_giving.wizard.opportunity.privacySettings.optionListedDescription') as string,
      boldLabel: true
    },
    {
      value: 'unlisted',
      label: t('workplace_giving.wizard.opportunity.privacySettings.optionUnlisted') as string,
      description: t('workplace_giving.wizard.opportunity.privacySettings.optionUnlistedDescription') as string,
      boldLabel: true
    }
  ]

  const isOpportunity = entityType === 'opportunity'
  const options = isOpportunity ? OPPORTUNITY_OPTIONS : FUNDRAISER_OPTIONS
  const displayOptions = !isOpportunity && !canMakePublic ? options.filter(opt => opt.value !== 'public') : options

  const label = isOpportunity
    ? t('workplace_giving.wizard.opportunity.privacySettings.label')
    : t('workplace_giving.wizard.fundraiser.privacySettings.label')

  return (
    <>
      <FormField
        label={label}
        necessity="required"
        status={touched && error ? 'danger' : 'default'}
        statusMessage={error}
        data-testid="privacyField"
        description={description}
      >
        <Select
          options={displayOptions}
          defaultValue={displayOptions.find(opt => opt.value === value)}
          onChange={option => onChange(option.value as FundraiserVisibility & OpportunityVisibility)}
          orientation="up"
          data-testid="privacySelect"
        />
      </FormField>
      {!isOpportunity && showPublicPermissionAlert && (
        <Alert
          variant="info"
          title={t('workplace_giving.wizard.fundraiser.privacySettings.noPermissionTitle')}
          data-testid="privacyNoPermissionAlert"
        >
          {t('workplace_giving.wizard.fundraiser.privacySettings.noPermissionBody')}
        </Alert>
      )}
    </>
  )
}
